import {PaymentInstructionStatus} from '@sollapay/enums';

import type {BuyerPaymentStatus} from '../types/buyers.types';
import type {PayResultReason} from '@sollapay/enums';
import type {PaymentInstructionDTO} from '@sollapay/types';
import type {IconName} from '@sollapay/ui/components';

interface PaymentStatusConfig {
  labelKey: string;
  variant: 'warning' | 'info' | 'success' | 'error';
  icon: IconName;
}

interface InstructionStatusConfig {
  variant: 'success' | 'warning' | 'error' | 'info' | 'progress' | 'neutral';
  icon: IconName;
}

/** Shared `ns`/`labelKeyPrefix` for every `PaymentMethodBadge` instance in this app. */
export const PAYMENT_METHOD_BADGE_I18N = {
  ns: 'trustAccounts',
  labelKeyPrefix: 'view.buyers.paymentRequest.methods'
} as const;

export const PAYMENT_INSTRUCTION_STATUS_CONFIG: Record<
  PaymentInstructionStatus,
  InstructionStatusConfig
> = {
  [PaymentInstructionStatus.COMPLETED]: {variant: 'success', icon: 'credit-card-check'},
  [PaymentInstructionStatus.NEEDS_REVIEW]: {variant: 'warning', icon: 'alert-circle'},
  [PaymentInstructionStatus.PARTIALLY_COMPLETED]: {variant: 'warning', icon: 'credit-card-check'},
  [PaymentInstructionStatus.FUNDS_IN_TRANSIT]: {variant: 'success', icon: 'credit-card-up'},
  [PaymentInstructionStatus.PENDING_EXECUTION]: {variant: 'info', icon: 'credit-card-up'},
  [PaymentInstructionStatus.EXECUTING]: {variant: 'info', icon: 'credit-card-up'},
  [PaymentInstructionStatus.REISSUING]: {variant: 'info', icon: 'credit-card-up'},
  [PaymentInstructionStatus.AWAITING_FUNDS]: {variant: 'info', icon: 'send'},
  [PaymentInstructionStatus.FAILED]: {variant: 'error', icon: 'alert-circle'},
  [PaymentInstructionStatus.REJECTED]: {variant: 'error', icon: 'alert-circle'},
  [PaymentInstructionStatus.CANCELLED]: {variant: 'error', icon: 'credit-card-cancel'},
  [PaymentInstructionStatus.EXPIRED]: {variant: 'error', icon: 'credit-card-expired'}
};

/**
 * Badge config for the "awaiting additional authorization" display state (PATC or
 * PENDING_ADDITIONAL_AUTHORIZATION). Not part of PAYMENT_INSTRUCTION_STATUS_CONFIG — the
 * underlying instruction status stays AWAITING_FUNDS, this only overrides the label/icon
 * shown to the lawyer. The specific reason drives the tooltip copy (different explanation
 * for "awaiting another signer" vs. "awaiting bank approval").
 */
const AWAITING_ADDITIONAL_AUTHORIZATION_STATUS_CONFIG: InstructionStatusConfig = {
  variant: 'warning',
  icon: 'users'
};

type InstructionTooltipKey = PaymentInstructionStatus | PayResultReason;

export interface InstructionStatusDisplay extends InstructionStatusConfig {
  statusKey: string;
  tooltipKey: InstructionTooltipKey | null;
}

/** Statuses that carry an explanatory tooltip alongside the badge */
const STATUSES_WITH_TOOLTIP = new Set<PaymentInstructionStatus>([
  PaymentInstructionStatus.PENDING_EXECUTION,
  PaymentInstructionStatus.EXECUTING,
  PaymentInstructionStatus.REISSUING,
  PaymentInstructionStatus.PARTIALLY_COMPLETED,
  PaymentInstructionStatus.NEEDS_REVIEW,
  PaymentInstructionStatus.FAILED,
  PaymentInstructionStatus.REJECTED,
  PaymentInstructionStatus.CANCELLED,
  PaymentInstructionStatus.EXPIRED,
  PaymentInstructionStatus.AWAITING_FUNDS,
  PaymentInstructionStatus.FUNDS_IN_TRANSIT,
  PaymentInstructionStatus.COMPLETED
]);

/**
 * Single source of truth for how to render an instruction's status badge + tooltip,
 * including the awaiting-additional-authorization override — keeps that check in one
 * place instead of re-derived at each render call site. The reason value itself (e.g.
 * `awaiting_additional_signers` vs `pending_additional_authorization`) is used directly
 * as the status/tooltip key, so each case gets its own accurate explanatory copy.
 */
export function getInstructionStatusDisplay(
  instruction: PaymentInstructionDTO
): InstructionStatusDisplay {
  if (instruction.awaitingAdditionalAuthorizationReason) {
    return {
      ...AWAITING_ADDITIONAL_AUTHORIZATION_STATUS_CONFIG,
      statusKey: instruction.awaitingAdditionalAuthorizationReason,
      tooltipKey: instruction.awaitingAdditionalAuthorizationReason
    };
  }

  return {
    ...PAYMENT_INSTRUCTION_STATUS_CONFIG[instruction.status],
    statusKey: instruction.status,
    tooltipKey: STATUSES_WITH_TOOLTIP.has(instruction.status) ? instruction.status : null
  };
}

const REQUEST_PENDING_STYLE = {variant: 'warning', icon: 'credit-card-edit'} as const;
const HELD_IN_TRUST_STYLE = {variant: 'success', icon: 'bank-note'} as const;

export const BUYER_PAYMENT_STATUS_CONFIG: Record<BuyerPaymentStatus, PaymentStatusConfig> = {
  no_request: {...REQUEST_PENDING_STYLE, labelKey: 'view.buyers.list.status.noRequest'},
  no_active_request: {
    ...REQUEST_PENDING_STYLE,
    labelKey: 'view.buyers.list.status.noActiveRequest'
  },
  request_sent: {
    labelKey: 'view.buyers.list.status.requestSent',
    variant: 'info',
    icon: 'credit-card-up'
  },
  partial_held: {...HELD_IN_TRUST_STYLE, labelKey: 'view.buyers.list.status.partial_held'},
  funds_held: {...HELD_IN_TRUST_STYLE, labelKey: 'view.buyers.list.status.funds_held'}
};
